// Import required libraries
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import './css/rectangular.css';

// Import parts
import init_player_el from './init_player_el';
import ivp_log from './utils/log';
import state from './state';
import * as Subs from './subtitles';
import * as Cues from './cues';
import seek_cue from './utils/seek_cue';
import * as sl_utils from './utils/sl_utils';
import './utils/conversion_utils';
import get_dom_element from './utils/get_dom_element';
import * as Resume from './resume';
import * as Seekbar from './seekbar';
import * as Visibility from './toggle_visibility';

// IVP Class definition
class IVP {
  // Set version
  static VERSION = VERSION;

  static getVersion() {
    return IVP.VERSION;
  }

  static log(msg, type) {
    ivp_log(msg, type);
  }

  static getResume(resumeID) {
    return Resume.get_resume_time(resumeID);
  }

  static setResume(resumeID, time) {
    return Resume.get_resume_time(resumeID, time);
  }

  constructor(element, options) {
    // Set initial values
    this.timeFormat = 1;

    // Init player <video-js> element
    const playerEl = init_player_el();

    // Get player parent element and add player to parent
    const playerParentEl = get_dom_element(element, true);
    playerParentEl.appendChild(playerEl);

    // Set element attributes
    playerEl.setAttribute('autobuffer', '');
    playerEl.setAttribute('playsinline', '');
    playerEl.setAttribute('preload', 'auto');
    playerEl.setAttribute('webkit-playsinline', '');

    // Create video.js player instance
    this.player = videojs(playerEl, options);

    this.player.on('dispose', () => {
      this.remove_seekbar();
    });

    IVP.log('IVP player initialized.', 'ok');
  }

  src(src) {
    this.player.src(src);
  }

  // Method to play video
  play() {
    this.player.play();
  }

  // Method to pause video
  pause() {
    this.player.pause();
  }

  // Method to set volume
  set_volume(volume) {
    IVP.log('[IVP] Setting volume to:  ' + volume, 'action');
    this.player.volume(volume);
  }

  // Method to mute or unmute video
  mute() {
    IVP.log('[IVP] Toggling mute.', 'action');
    this.player.muted(!this.player.muted());
  }

  // Method to seek to specific time
  seek_to(time) {
    IVP.log('[IVP] Seeking to:  ' + time + ', ' + time.toHHMMSS(), 'action');
    this.player.currentTime(time);
  }

  // Method to dispose of video player instance
  dispose() {
    IVP.log('[IVP] Disposing of IVP player.', 'action');
    this.remove_seekbar();
    this.player.dispose();
  }

  // Method to register event listeners
  on(eventName, listener) {
    this.player.on(eventName, listener);
  }

  // Method to remove event listeners
  off(eventName, listener) {
    this.player.off(eventName, listener);
  }

  // Method to get state of video player
  get_state() {
    return state(this.player);
  }

  // Subtitle methods
  /**
   * Load subtitle from a given URL. The function can also check for existing subtitles before adding them.
   *
   * @param {string} lang - The language code for the subtitle.
   * @param {string} label - The label for the subtitle.
   * @param {string} url - The URL where the subtitle file is located.
   * @param {boolean} enabled - A boolean indicating if the subtitle should be enabled by default.
   * @param {boolean} [checkExisting=false] - An optional parameter indicating if the function should check for existing subtitles before adding.
   *
   * @returns {void}
   */
  load_sub(lang, label, url, enabled, checkExisting) {
    IVP.log('[IVP] Loading subtitle:  ' + label + ' (' + lang + ')', 'action');
    Subs.load_subtitle(this.player, lang, label, url, enabled, checkExisting);
  }

  /**
   * Toggles the display state of subtitle tracks for a given player.
   *
   * @param {boolean} [state] - The desired display state of the subtitles. If true, the subtitles are shown. If false, the subtitles are hidden. If not provided, the function will toggle the current state.
   *
   * @returns {void}
   */
  toggle_sub(state) {
    IVP.log('[IVP] Toggling subtitle.', 'action');
    Subs.toggle_subtitle(this.player, state);
  }

  /**
   * Toggles the visibility of all subtitles for all video players.
   *
   * @returns {void}
   */
  toggle_all_subs() {
    IVP.log('[IVP] Toggling all subtitles.', 'action');
    Subs.toggle_all_subtitles();
  }

  /**
   * Removes subtitle tracks from a given player. If language codes are specified, only the subtitles of those languages are removed.
   *
   * @param {Array} [langs] - An array of language codes of the subtitles to be removed. If not provided or empty, all subtitle tracks are removed.
   *
   * @returns {void}
   */
  remove_subtitles(langs = []) {
    IVP.log('[IVP] Removing subtitles: ' + langs, 'action');
    Subs.remove_subtitles(this.player, langs);
  }

  /**
   * Add Chapters
   * @param {string} chapters - WebVTT-formatted string
   */
  add_chapters(chapters) {
    IVP.log('[IVP] Adding chapters', 'action');
    Cues.add_chapters(this.player, chapters);
  }

  // Seek chapter
  seek_chapter(id) {
    IVP.log('[IVP] Seeking chapter ' + id, 'action');
    seek_cue(this.player, 'chapter', id);
  }

  /**
   * Add Interactions
   * @param {string} interactions - WebVTT-formatted string
   */
  add_interactions(interactions) {
    IVP.log('[IVP] Adding interactions', 'action');
    Cues.add_interactions(this.player, interactions);
  }

  // Seek interaction
  seek_interaction(id) {
    IVP.log('[IVP] Seeking interaction ' + id, 'action');
    seek_cue(this.player, 'interaction', id);
  }

  /**
   * Add Completion
   * @param {string} completion - WebVTT-formatted string
   */
  add_completion(completion) {
    IVP.log('[IVP] Adding completion', 'action');
    Cues.add_completion(this.player, completion);
  }

  /**
   * Load Storyline helpers
   * @param {string} chapters - WebVTT-formatted string
   */
  load_storyline_utils() {
    IVP.log('[IVP] Loading storyline utils', 'action');
    sl_utils.events(this);
  }

  toggle_time_display_format() {
    IVP.log('[IVP] Toggling time display format', 'action');
    const numFormats = 3; // how many formats there are
    this.timeFormat = (this.timeFormat + 1) % numFormats;
    sl_utils.update_timers(this);
  }

  // Seekbar methods
  add_seekbar(el, color) {
    IVP.log('[IVP] Adding seekbar', 'action');
    Seekbar.add_seekbar(this, el, color);
  }

  update_seekbar(seconds) {
    Seekbar.update_seekbar(this, seconds);
  }

  update_seekbar_visibility(state) {
    Seekbar.update_seekbar_visibility(this, state);
  }

  remove_seekbar() {
    IVP.log('[IVP] Removing seekbar', 'action');
    Seekbar.remove_seekbar(this);
  }

  // Hide player
  hide() {
    IVP.log('[IVP] Hiding player', 'action');
    Visibility.hide(this);
  }

  // Show player
  show() {
    IVP.log('[IVP] Showing player', 'action');
    Visibility.show(this);
  }
}

//window.IVP = IVP;
var global = window || global;
global.IVP = IVP;
